import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Target, Scale, MapPin, Building2 } from 'lucide-react';
import Card from '../components/ui/Card';
import { useSEO } from '../hooks/useSEO';

export default function About() {
  const { t } = useLanguage();
  useSEO({
    title: 'About Us',
    description:
      'Learn about GRAWEO Charitable Foundation — our mission, goals, and team dedicated to supporting people who have experienced difficult circumstances.',
    video: {
      name: 'GRAWEO Charitable Foundation — Mission and Goals',
      description:
        'GRAWEO Charitable Foundation provides humanitarian aid, reintegration support, and livelihood recovery for people affected by conflict in Ukraine.',
      embedUrl: 'https://www.youtube.com/embed/TDTalQjIhMA',
      contentUrl: 'https://www.youtube.com/watch?v=TDTalQjIhMA',
      thumbnailUrl: 'https://img.youtube.com/vi/TDTalQjIhMA/maxresdefault.jpg',
      uploadDate: '2024-03-01',
    },
  });

  const goals = [
    t('goal1'),
    t('goal2'),
    t('goal3'),
    t('goal4'),
  ];

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-graweo-chocolate to-graweo-olive text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold font-manrope mb-6">
              {t('nav_about')}
            </h1>
            <p className="text-xl text-gray-100 leading-relaxed">
              {t('about_description')}
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-graweo-terra/10 text-graweo-terra px-4 py-2 rounded-full mb-6">
                <Target className="w-4 h-4" />
                <span className="text-sm font-semibold">{t('mission_title')}</span>
              </div>
              <h2 className="text-3xl font-bold font-manrope text-gray-900 mb-6">
                {t('mission_title')}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                {t('mission_text')}
              </p>
            </div>
            <div className="relative w-full rounded-2xl overflow-hidden shadow-xl" style={{ paddingTop: '56.25%' }}>
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube.com/embed/TDTalQjIhMA"
                title="GRAWEO Charitable Foundation — Mission and Goals"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* Goals */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-gray-900 mb-12 text-center">
            {t('goals_title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {goals.map((goal, index) => (
              <Card key={index} className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-graweo-terra to-graweo-terra/80 rounded-xl flex items-center justify-center text-white font-bold">
                    {index + 1}
                  </div>
                  <p className="text-gray-700 leading-relaxed pt-2">
                    {goal}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Information */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold font-manrope text-gray-900 mb-12 text-center">
            {t('legal_title')}
          </h2>

          <Card className="p-8 max-w-3xl mx-auto">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <Building2 className="w-6 h-6 text-graweo-chocolate flex-shrink-0 mt-1" />
                <div>
                  <div className="font-semibold text-gray-900 mb-1">{t('legal_name')}</div>
                  <div className="text-gray-600">{t('legal_name_value')}</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Scale className="w-6 h-6 text-graweo-chocolate flex-shrink-0 mt-1" />
                <div>
                  <div className="font-semibold text-gray-900 mb-1">{t('legal_registration')}</div>
                  <div className="text-gray-600">Foundation Registration Number 45014804</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <MapPin className="w-6 h-6 text-graweo-chocolate flex-shrink-0 mt-1" />
                <div>
                  <div className="font-semibold text-gray-900 mb-1">{t('legal_country')}</div>
                  <div className="text-gray-600">{t('legal_country_value')}</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <MapPin className="w-6 h-6 text-graweo-chocolate flex-shrink-0 mt-1" />
                <div>
                  <div className="font-semibold text-gray-900 mb-1">{t('legal_address')}</div>
                  <div className="text-gray-600">
                    <div>{t('legal_address_value')}</div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

    </div>
  );
}
